import { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { useUserRole } from "@/hooks/useUserRole";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Shield } from "lucide-react";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { UsersTable } from "@/components/admin/UsersTable";
import { DashboardStats } from "@/components/admin/DashboardStats";
import { ExportButton } from "@/components/admin/ExportButton";
import { PWAAnalytics } from "@/components/admin/PWAAnalytics";
import { VersionInfo } from "@/components/admin/VersionInfo";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

type UserRole = 'admin' | 'paid' | 'student' | 'free';

interface UserWithRole {
  id: string;
  email: string;
  created_at: string;
  role: UserRole | null;
  isBanned: boolean;
  banReason: string | null;
  usage: {
    totalTokens: number;
    totalCost: number;
    queryCount: number;
  };
}

export default function Admin() {
  const { user, isLoading: authLoading } = useAuth();
  const { hasAccess, isLoading: roleLoading } = useUserRole();
  const [users, setUsers] = useState<UserWithRole[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [roleToChange, setRoleToChange] = useState<{ userId: string; newRole: UserRole } | null>(null);
  const [banAction, setBanAction] = useState<{ userId: string; email: string; action: 'ban' | 'unban' } | null>(null);
  const [banReason, setBanReason] = useState("");
  const [deleteAction, setDeleteAction] = useState<{ userId: string; email: string } | null>(null);
  const [deleteReason, setDeleteReason] = useState("");
  const [dashboardStats, setDashboardStats] = useState({
    totalVisits: 0,
    expectedMonthlyIncome: 0,
    totalSuperCalcQueries: 0,
    totalSuperCalcCost: 0,
  });

  const isAdmin = hasAccess(['admin']);

  useEffect(() => {
    if (isAdmin) {
      fetchUsers();
      fetchDashboardStats();
    }
  }, [isAdmin]);

  const fetchDashboardStats = async () => {
    try {
      // Fetch total visits count
      const { count: totalVisits } = await supabase
        .from('page_visits')
        .select('*', { count: 'exact', head: true });

      // Fetch all SuperCalc usage
      const { data: usageData, error: usageError } = await supabase
        .from('supercalc_usage')
        .select('tokens_used, cost_usd');

      let totalQueries = 0;
      let totalCost = 0;
      if (!usageError && usageData) {
        totalQueries = usageData.length;
        totalCost = usageData.reduce((sum, item) => sum + (item.cost_usd || 0), 0);
      }

      setDashboardStats({
        totalVisits: totalVisits || 0,
        expectedMonthlyIncome: 0, // Will calculate from users
        totalSuperCalcQueries: totalQueries,
        totalSuperCalcCost: totalCost,
      });
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      setIsLoadingUsers(true);
      
      // Call edge function to get users (requires service role key)
      const { data, error } = await supabase.functions.invoke('list-users', {
        headers: {
          Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        }
      });
      
      if (error) {
        console.error('Error fetching users:', error);
        toast.error("Failed to fetch users");
        return;
      }

      if (data?.error) {
        console.error('Error from function:', data.error);
        toast.error(data.error);
        return;
      }

      const usersList = data.users || [];
      setUsers(usersList);

      // Calculate expected monthly income
      const income = usersList.reduce((total, user) => {
        if (user.role === 'student') return total + 4.40;
        if (user.role === 'paid') return total + 8.80;
        return total;
      }, 0);

      setDashboardStats(prev => ({ ...prev, expectedMonthlyIncome: income }));
    } catch (error) {
      console.error('Error in fetchUsers:', error);
      toast.error("Failed to load users");
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const handleRoleChange = async () => {
    if (!roleToChange) return;

    try {
      const { userId, newRole } = roleToChange;

      // First, delete any existing role for this user
      const { error: deleteError } = await supabase
        .from('user_roles')
        .delete()
        .eq('user_id', userId);

      if (deleteError) throw deleteError;

      // Then insert the new role
      const { error: insertError } = await supabase
        .from('user_roles')
        .insert([{ user_id: userId, role: newRole as any }]);

      if (insertError) throw insertError;

      toast.success(`User role updated to ${newRole}`);
      fetchUsers();
    } catch (error) {
      console.error('Error updating role:', error);
      toast.error("Failed to update user role");
    } finally {
      setRoleToChange(null);
    }
  };

  const handleBanUser = async () => {
    if (!banAction || !user) return;

    try {
      if (banAction.action === 'ban') {
        if (!banReason.trim()) {
          toast.error("Please provide a reason for the ban");
          return;
        }

        const { error } = await supabase
          .from('user_bans')
          .insert([{
            user_id: banAction.userId,
            banned_by: user.id,
            reason: banReason
          }]);

        if (error) throw error;
        toast.success(`User ${banAction.email} has been banned from SuperCalc`);
      } else {
        const { error } = await supabase
          .from('user_bans')
          .delete()
          .eq('user_id', banAction.userId);

        if (error) throw error;
        toast.success(`User ${banAction.email} has been unbanned`);
      }

      fetchUsers();
    } catch (error) {
      console.error('Error managing ban:', error);
      toast.error("Failed to update ban status");
    } finally {
      setBanAction(null);
      setBanReason("");
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteAction || !user) return;

    try {
      if (!deleteReason.trim()) {
        toast.error("Please provide a reason for deletion");
        return;
      }

      const { data, error } = await supabase.functions.invoke('delete-account', {
        body: { 
          userId: deleteAction.userId,
          reason: deleteReason
        },
        headers: {
          Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        }
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast.success(`User ${deleteAction.email} has been permanently deleted`);
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error("Failed to delete user");
    } finally {
      setDeleteAction(null);
      setDeleteReason("");
    }
  };


  if (authLoading || roleLoading) {
    return (
      <PageLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </PageLayout>
    );
  }

  // Redirect unauthorized users immediately
  if (!user || !isAdmin) {
    return <Navigate to="/" replace />;
  }

  const usersByRole = users.reduce(
    (acc, user) => {
      const role = user.role || 'free';
      acc[role] = (acc[role] || 0) + 1;
      return acc;
    },
    { free: 0, student: 0, paid: 0, admin: 0 }
  );

  return (
    <PageLayout>
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="h-6 w-6 text-primary" />
            <div>
              <h1 className="text-3xl font-bold">Admin Dashboard</h1>
              <p className="text-muted-foreground">Manage users and monitor platform analytics</p>
            </div>
          </div>
          <ExportButton users={users} />
        </div>

        {!isLoadingUsers && (
          <DashboardStats
            totalUsers={users.length}
            usersByRole={usersByRole}
            totalVisits={dashboardStats.totalVisits}
            expectedMonthlyIncome={dashboardStats.expectedMonthlyIncome}
            totalSuperCalcQueries={dashboardStats.totalSuperCalcQueries}
            totalSuperCalcCost={dashboardStats.totalSuperCalcCost}
          />
        )}

        <Tabs defaultValue="users" className="w-full">
          <TabsList className="grid w-full grid-cols-2 max-w-md">
            <TabsTrigger value="users">User Management</TabsTrigger>
            <TabsTrigger value="pwa">PWA Analytics</TabsTrigger>
          </TabsList>

          <TabsContent value="users" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>User Management</CardTitle>
                <CardDescription>Manage user roles and permissions</CardDescription>
              </CardHeader>
              <CardContent>
                {isLoadingUsers ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                  </div>
                ) : (
                  <UsersTable
                    users={users}
                    currentUserId={user?.id}
                    onRoleChange={(userId, newRole) => setRoleToChange({ userId, newRole })}
                    onBan={(userId, email) => setBanAction({ userId, email, action: 'ban' })}
                    onUnban={(userId, email) => setBanAction({ userId, email, action: 'unban' })}
                    onDelete={(userId, email) => setDeleteAction({ userId, email })}
                  />
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="pwa" className="mt-6">
            <PWAAnalytics />
          </TabsContent>
        </Tabs>

        <VersionInfo />
      </div>

      {/* Role Change Dialog */}
      <AlertDialog open={!!roleToChange} onOpenChange={() => setRoleToChange(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Role Change</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to change this user's role to {roleToChange?.newRole}?
              This will update their access permissions immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRoleChange}>Confirm</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Ban/Unban Dialog */}
      <AlertDialog open={!!banAction} onOpenChange={() => {
        setBanAction(null);
        setBanReason("");
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {banAction?.action === 'ban' ? 'Ban User from SuperCalc' : 'Unban User'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {banAction?.action === 'ban' ? (
                <>
                  This will prevent {banAction.email} from accessing SuperCalc. They will still be able to use other calculators.
                  <div className="mt-4">
                    <label htmlFor="ban-reason" className="text-sm font-medium text-foreground">
                      Reason for ban:
                    </label>
                    <Input
                      id="ban-reason"
                      value={banReason}
                      onChange={(e) => setBanReason(e.target.value)}
                      placeholder="e.g., Excessive usage, abuse of terms..."
                      className="mt-2"
                    />
                  </div>
                </>
              ) : (
                `Are you sure you want to unban ${banAction?.email}? They will regain access to SuperCalc.`
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleBanUser}
              className={banAction?.action === 'ban' ? 'bg-destructive hover:bg-destructive/90' : ''}
            >
              {banAction?.action === 'ban' ? 'Ban User' : 'Unban User'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete User Dialog */}
      <AlertDialog open={!!deleteAction} onOpenChange={() => {
        setDeleteAction(null);
        setDeleteReason("");
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-destructive">Delete User Permanently</AlertDialogTitle>
            <AlertDialogDescription>
              <div className="space-y-4">
                <p className="font-semibold text-foreground">
                  ⚠️ WARNING: This action cannot be undone!
                </p>
                <p>
                  This will permanently delete {deleteAction?.email} and all their associated data including:
                </p>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>Account credentials</li>
                  <li>Favorites</li>
                  <li>SuperCalc usage history</li>
                  <li>Roles and permissions</li>
                  <li>All other user data</li>
                </ul>
                <div className="mt-4">
                  <label htmlFor="delete-reason" className="text-sm font-medium text-foreground">
                    Reason for deletion (required):
                  </label>
                  <Select value={deleteReason} onValueChange={setDeleteReason}>
                    <SelectTrigger className="mt-2">
                      <SelectValue placeholder="Select a reason..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Fake Account">Fake Account</SelectItem>
                      <SelectItem value="Abusing SuperCalc">Abusing SuperCalc</SelectItem>
                      <SelectItem value="Threatened CalcLab">Threatened CalcLab</SelectItem>
                      <SelectItem value="Terms of Service Violation">Terms of Service Violation</SelectItem>
                      <SelectItem value="Spam/Bot Activity">Spam/Bot Activity</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteUser}
              className="bg-destructive hover:bg-destructive/90"
            >
              Delete User Permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageLayout>
  );
}
